import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Star } from 'lucide-react';
import type { WidgetReview } from '@/types/firestore';

interface ReviewsCarouselProps {
  reviews: WidgetReview[];
  intervalMs?: number;
}

export function ReviewsCarousel({ reviews, intervalMs = 5000 }: ReviewsCarouselProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  useEffect(() => {
    if (reviews.length <= 1) return;

    const timer = setInterval(() => {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % reviews.length);
        setIsTransitioning(false);
      }, 300);
    }, intervalMs);

    return () => clearInterval(timer);
  }, [reviews.length, intervalMs]);

  if (!reviews || reviews.length === 0) return null;

  const review = reviews[currentIndex];
  const initials = review.reviewer_name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const rating = review.rating ?? 5;

  return (
    <Card className="p-4 bg-background/50 backdrop-blur-sm border-border/40">
      <div
        className={`transition-opacity duration-300 ${
          isTransitioning ? 'opacity-0' : 'opacity-100'
        }`}
      >
        {/* Stars */}
        <div className="flex gap-0.5 mb-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <Star
              key={i}
              className={`h-4 w-4 ${
                i < rating ? 'fill-yellow-400 text-yellow-400' : 'text-muted-foreground/30'
              }`}
            />
          ))}
        </div>

        {/* Review Text */}
        <p className="text-sm text-foreground/90 italic mb-4 line-clamp-3">
          "{review.text}"
        </p>

        {/* Reviewer */}
        <div className="flex items-center gap-3">
          <Avatar className="h-8 w-8">
            {review.avatar_url && <AvatarImage src={review.avatar_url} alt={review.reviewer_name} />}
            <AvatarFallback className="text-xs bg-primary/10 text-primary">
              {initials}
            </AvatarFallback>
          </Avatar>
          <div>
            <p className="text-sm font-medium text-foreground">{review.reviewer_name}</p>
            {review.date && (
              <p className="text-xs text-muted-foreground">{review.date}</p>
            )}
          </div>
        </div>
      </div>

      {/* Dots indicator */}
      {reviews.length > 1 && (
        <div className="flex justify-center gap-1.5 mt-4">
          {reviews.map((_, i) => (
            <button
              key={i}
              onClick={() => {
                setIsTransitioning(true);
                setTimeout(() => {
                  setCurrentIndex(i);
                  setIsTransitioning(false);
                }, 300);
              }}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                i === currentIndex
                  ? 'w-4 bg-primary'
                  : 'w-1.5 bg-muted-foreground/30 hover:bg-muted-foreground/50'
              }`}
              aria-label={`Go to review ${i + 1}`}
            />
          ))}
        </div>
      )}
    </Card>
  );
}
